/**
 * Payment Service
 *
 * Handles payment order API calls:
 * - Payment order creation
 * - Payment status tracking
 * - Payment history
 * - Admin payment verification
 * - T-Payment integration
 *
 * @see WICL-DOC-Payment QR Code and Bank Transfer Payment Flow
 */

import { api, apiClient } from "@/lib/api";
import type {
  PaymentOrder,
  CreatePaymentOrderRequest,
  CreatePaymentOrderResponse,
  CancelPaymentRequest,
  PaginatedResponse,
  PaymentHistoryParams,
  QrProviderInfo,
  BankTransferInfo,
  OrderPaymentDto,
} from "@/types/payment";

/** The payment still open against an order, as `GET /payments/orders/by-order/{id}/pending` returns it. */
export interface PendingPaymentForOrder {
  paymentId: string;
  paymentOrderNumber: string;
  hasProof: boolean;
  /** "BankTransfer" | "QRCode" — decides whether continuing can reopen it or must cancel. */
  method: string;
}

/** Refusals every payment screen answers with `AwaitingPaymentDialog`, so the api client stays quiet. */
const OPEN_PAYMENT_REFUSALS = [
  "TENANT_PAYMENT_ALREADY_OPEN",
  "ORDER_PAYMENT_ALREADY_OPEN",
];

/**
 * Payment Service Methods
 */
export const paymentService = {
  /**
   * Create a new payment order
   * User selects payment method and amount, system creates order and returns T-Payment redirect URL
   */
  createPaymentOrder: (
    request: CreatePaymentOrderRequest,
  ): Promise<CreatePaymentOrderResponse> => {
    // `_skipServerRetry` on every write in this service: a 5xx can arrive after the payment was
    // committed, and the interceptor's automatic retry would open or charge a second one.
    // The open-payment refusals are answered by every caller with `AwaitingPaymentDialog`.
    return api.post("/payments/orders", request, {
      _skipServerRetry: true,
      _suppressErrorToastFor: OPEN_PAYMENT_REFUSALS,
    });
  },

  /**
   * Get payment order by ID
   * Used for status tracking and polling
   */
  getPaymentOrder: (paymentId: string): Promise<PaymentOrder> => {
    return api.get(`/payments/orders/${paymentId}`);
  },

  /**
   * Get the most recent payment awaiting verification for an order, if any — checks
   * Payments directly since order.paymentId isn't populated for client-initiated payments.
   */
  getPendingPaymentForOrder: async (
    orderId: string,
  ): Promise<PendingPaymentForOrder | null> => {
    // Read the envelope directly rather than through `api.get`: "no open payment" is answered with
    // `data: null`, and `api.get` falls back to the whole envelope when `data` is falsy — a truthy
    // object that made every order look like it already had a payment open.
    const response = await apiClient.get<{
      data: PendingPaymentForOrder | null;
    }>(`/payments/orders/by-order/${orderId}/pending`);
    return response.data.data ?? null;
  },

  /**
   * Get payment history for current tenant
   * Returns paginated list of payment orders
   */
  getPaymentHistory: async (
    params?: PaymentHistoryParams,
  ): Promise<PaginatedResponse<PaymentOrder>> => {
    const result = await api.getList<PaymentOrder>("/payments/history", {
      params,
    });
    return {
      data: result.data,
      totalCount: result.pagination.totalItems,
      page: result.pagination.currentPage,
      pageSize: result.pagination.itemsPerPage,
      totalPages: result.pagination.totalPages,
    };
  },

  /**
   * Cancel payment
   * User can cancel their own pending payment
   */
  cancelPayment: (
    paymentId: string,
    request: CancelPaymentRequest,
  ): Promise<void> => {
    // A replay of a cancel that went through is refused, and would report a failure for it.
    return api.post(`/payments/${paymentId}/cancel`, request, {
      _skipServerRetry: true,
    });
  },

  /**
   * Submit payment proof for verification
   * Associates uploaded proof with payment order
   *
   * @param paymentId - Payment order UUID
   * @param attachmentIds - Array of attachment IDs from upload responses
   */
  submitPaymentProof: async (
    paymentId: string,
    attachmentIds: string[],
  ): Promise<void> => {
    await api.post(
      `/payments/orders/${paymentId}/submit-proof`,
      { attachmentIds },
      { _skipServerRetry: true },
    );
  },

  /**
   * Remove one proof file from a payment (soft-deletes the attachment reference).
   * The API only allows this for a ref owned by the caller's tenant, and only while the
   * payment is still Pending or AwaitingVerification.
   *
   * @param refId - Attachment reference ID, from PaymentAttachment.refId
   */
  removeProofAttachment: async (refId: string): Promise<void> => {
    await api.delete(`/attachments/refs/${refId}`, { _skipServerRetry: true });
  },

  /**
   * Pay order with account balance
   * Deducts amount due (order.Total) from the tenant's bonus first, then cash balance,
   * marks the order Paid and triggers provisioning. The amount is resolved server-side
   * from the order — no amount is sent. Returns the authoritative paid amount + new balance.
   *
   * @param orderId - Order UUID
   */
  payWithBalance: (orderId: string): Promise<OrderPaymentDto> => {
    return api.post(`/payments/orders/${orderId}/pay-with-balance`, undefined, {
      _skipServerRetry: true,
      _suppressErrorToastFor: OPEN_PAYMENT_REFUSALS,
    });
  },

  /**
   * Get available QR payment providers.
   * T-Payment is always available. T-PayGate is available when configured.
   */
  getQrProviders: (): Promise<QrProviderInfo[]> => {
    return api.get("/payments/qr-providers");
  },

  /**
   * Get the bank account customers must transfer to.
   * Configured by the admin under platform settings → Bank Transfer Settings.
   * Returns available=false (not an error) when it is not configured.
   */
  getBankTransferInfo: (): Promise<BankTransferInfo> => {
    return api.get("/payments/bank-transfer-info");
  },
};
